import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EmptyPanel } from '@/components/px/EmptyPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { isoDayLabel } from '@/features/projects/format'
import { selectCanWriteProject } from '@/features/projects/slices/projectDetailSlice'
import { useAppDispatch, useAppSelector } from '@/store'
import {
  archiveVersion,
  activateVersion,
  createVersion,
  deleteVersion,
  fetchVersions,
  selectVersions,
  selectVersionsError,
  selectVersionsListError,
  selectVersionsLoading,
  selectVersionsMutating,
  selectVersionsNonce,
  versionsErrorDismissed,
  versionsRetryArmed
} from '../slices/versionsSlice'

interface CreateForm {
  /** The version slug (wire `id`) — required by the server's @NotBlank. */
  id: string
  name: string
  description: string
  startDate: string
  targetDate: string
}

/**
 * The work-packages panel (AC-4): versions as work packages, with create /
 * activate / archive / delete for callers the server says may write. The
 * bus nonce (`version.*` frames) and every mutation refetch the list — no
 * optimistic version writes (the ACTIVE chain is server-validated).
 */
export function VersionsPanel({ orgId, projectId }: { orgId: string; projectId: string }) {
  const { t } = useTranslation('projectWorkspace')
  const dispatch = useAppDispatch()
  const versions = useAppSelector(selectVersions)
  const loading = useAppSelector(selectVersionsLoading)
  const listError = useAppSelector(selectVersionsListError)
  const mutationError = useAppSelector(selectVersionsError)
  const mutating = useAppSelector(selectVersionsMutating)
  const nonce = useAppSelector(selectVersionsNonce)
  const canWrite = useAppSelector(selectCanWriteProject)

  const [formOpen, setFormOpen] = useState(false)
  const [form, setForm] = useState<CreateForm>({ id: '', name: '', description: '', startDate: '', targetDate: '' })
  const [formError, setFormError] = useState<string | null>(null)

  // Mount + bus-nonce refetch (the fetchCache condition dedupes redundant
  // dispatches; the nonce only moves when a frame or mutation invalidated).
  useEffect(() => {
    void dispatch(fetchVersions({ orgId, projectId }))
  }, [dispatch, orgId, projectId, nonce])

  const onCreateClicked = async (): Promise<void> => {
    // The server requires BOTH the slug (wire `id`) and the name
    // (`@NotBlank` on CreateVersionRequest) — validated client-side too so
    // the form can never post a guaranteed 400.
    if (form.id.trim() === '') {
      setFormError(t('versions.idRequired'))
      return
    }
    if (form.name.trim() === '') {
      setFormError(t('versions.nameRequired'))
      return
    }
    setFormError(null)
    const result = await dispatch(
      createVersion({
        orgId,
        projectId,
        input: {
          id: form.id.trim(),
          name: form.name.trim(),
          description: form.description.trim() === '' ? undefined : form.description.trim(),
          startDate: form.startDate === '' ? undefined : form.startDate,
          targetDate: form.targetDate === '' ? undefined : form.targetDate
        }
      })
    )
    if (createVersion.fulfilled.match(result)) {
      setFormOpen(false)
      setForm({ id: '', name: '', description: '', startDate: '', targetDate: '' })
    }
  }

  return (
    <section aria-label={t('versions.panelTitle')} className="rounded-lg border border-border bg-card px-4 py-3.5">
      <div className="flex items-baseline justify-between gap-3">
        <SectionLabel>{t('versions.panelTitle')}</SectionLabel>
        {canWrite && (
          <button
            type="button"
            onClick={() => setFormOpen((open) => !open)}
            className="text-xs font-medium text-accent-foreground hover:underline"
            data-testid="version-create-open"
          >
            {t('versions.create')}
          </button>
        )}
      </div>

      {formOpen && canWrite && (
        <div className="mt-2 rounded-md border border-border px-3 py-2.5">
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <label htmlFor="version-id" className="block text-xs text-muted-foreground">
                {t('versions.id')}
              </label>
              <input
                id="version-id"
                value={form.id}
                onChange={(event) => setForm((f) => ({ ...f, id: event.target.value }))}
                placeholder={t('versions.idPlaceholder')}
                className="mt-0.5 w-full rounded-md border border-input bg-card px-2.5 py-1.5 font-mono text-sm text-foreground"
              />
            </div>
            <div>
              <label htmlFor="version-name" className="block text-xs text-muted-foreground">
                {t('versions.name')}
              </label>
              <input
                id="version-name"
                value={form.name}
                onChange={(event) => setForm((f) => ({ ...f, name: event.target.value }))}
                className="mt-0.5 w-full rounded-md border border-input bg-card px-2.5 py-1.5 text-sm text-foreground"
              />
            </div>
            <div>
              <label htmlFor="version-start" className="block text-xs text-muted-foreground">
                {t('versions.startDate')}
              </label>
              <input
                id="version-start"
                type="date"
                value={form.startDate}
                onChange={(event) => setForm((f) => ({ ...f, startDate: event.target.value }))}
                className="mt-0.5 w-full rounded-md border border-input bg-card px-2.5 py-1.5 text-sm text-foreground"
              />
            </div>
            <div>
              <label htmlFor="version-target" className="block text-xs text-muted-foreground">
                {t('versions.targetDate')}
              </label>
              <input
                id="version-target"
                type="date"
                value={form.targetDate}
                onChange={(event) => setForm((f) => ({ ...f, targetDate: event.target.value }))}
                className="mt-0.5 w-full rounded-md border border-input bg-card px-2.5 py-1.5 text-sm text-foreground"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="version-description" className="block text-xs text-muted-foreground">
                {t('versions.description')}
              </label>
              <textarea
                id="version-description"
                rows={2}
                value={form.description}
                onChange={(event) => setForm((f) => ({ ...f, description: event.target.value }))}
                className="mt-0.5 w-full rounded-md border border-input bg-card px-2.5 py-1.5 text-sm text-foreground"
              />
            </div>
          </div>
          {formError !== null && (
            <p role="alert" className="mt-1.5 text-xs text-rose-ink">
              {formError}
            </p>
          )}
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setFormOpen(false)}
              className="rounded-md border border-input px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted"
            >
              {t('versions.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void onCreateClicked()}
              disabled={mutating}
              className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              data-testid="version-create-submit"
            >
              {mutating ? t('versions.creating') : t('versions.submit')}
            </button>
          </div>
        </div>
      )}

      {listError !== null && (
        <div role="alert" className="mt-2 rounded-md border border-blocked bg-blocked-bg px-3 py-2 text-sm text-foreground">
          <p>{t('versions.serverError')}: {listError.message}</p>
          <button
            type="button"
            onClick={() => {
              dispatch(versionsRetryArmed())
              void dispatch(fetchVersions({ orgId, projectId }))
            }}
            className="mt-1 text-xs font-medium text-accent-foreground hover:underline"
          >
            {t('retry')}
          </button>
        </div>
      )}
      {mutationError !== null && (
        <p role="alert" className="mt-2 rounded-md border border-blocked bg-blocked-bg px-3 py-2 text-sm text-foreground">
          {t('versions.serverError')}: {mutationError.message}
          <button
            type="button"
            onClick={() => dispatch(versionsErrorDismissed())}
            className="ml-2 text-xs font-medium text-accent-foreground hover:underline"
          >
            ×
          </button>
        </p>
      )}

      {versions.length === 0 ? (
        /* PX-W6.6 (AC-2): the SHARED panels, so the workspace announces its
           wait and its emptiness in the same grammar as every other surface. */
        <div className="mt-1.5" data-testid="versions-empty">
          {loading ? (
            <LoadingPanel label={t('loading')} rows={2} />
          ) : (
            <EmptyPanel body={t('versions.empty')} />
          )}
        </div>
      ) : (
        <ul className="mt-1.5" data-testid="versions-list">
          {versions.map((version) => (
            <li key={version.id} className="v-row flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-foreground">{version.name}</span>
                {version.description !== undefined && (
                  <span className="block truncate text-xs text-muted-foreground">{version.description}</span>
                )}
                <span className="block text-xs text-muted-foreground">
                  {isoDayLabel(version.startDate)} → {isoDayLabel(version.targetDate)}
                </span>
              </span>
              <StatusPill tone={version.status === 'ACTIVE' ? 'done' : version.status === 'ARCHIVED' ? 'neutral' : 'proposed'}>
                {version.status === 'ACTIVE'
                  ? t('versions.active')
                  : version.status === 'ARCHIVED'
                    ? t('versions.archived')
                    : (version.status ?? '—')}
              </StatusPill>
              {canWrite && (
                <span className="flex shrink-0 gap-2 text-xs">
                  {version.status !== 'ACTIVE' && (
                    <button
                      type="button"
                      disabled={mutating}
                      onClick={() => void dispatch(activateVersion({ orgId, projectId, versionId: version.id }))}
                      className="font-medium text-accent-foreground hover:underline disabled:opacity-50"
                    >
                      {t('versions.activate')}
                    </button>
                  )}
                  {version.status !== 'ARCHIVED' && (
                    <button
                      type="button"
                      disabled={mutating}
                      onClick={() => void dispatch(archiveVersion({ orgId, projectId, versionId: version.id }))}
                      className="font-medium text-accent-foreground hover:underline disabled:opacity-50"
                    >
                      {t('versions.archive')}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={mutating}
                    onClick={() => void dispatch(deleteVersion({ orgId, projectId, versionId: version.id }))}
                    className="font-medium text-accent-foreground hover:underline disabled:opacity-50"
                  >
                    {t('versions.delete')}
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {!canWrite && <p className="mt-2 text-xs text-muted-foreground">{t('versions.readonly')}</p>}
    </section>
  )
}
